import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import * as paymentInputActionCreators from '@makemydeal/dr-dash-store/actions/paymentInput';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useSelector } from 'react-redux';

import { CASH_AMOUNT_PAID, CASH_DOWN } from '../../constants';
import { dealLockHooks } from '../../hooks';
import { usePaymentCall } from '../../utils/usePaymentCall';

export const useSummaryCashAmountPaid = ({ label }: { label?: string }) => {
    const isTargetCashAmountPaid = useSelector(dueAtSigningSelectors.getTargetCashAmountPaidOverride);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const deductDueAtSigning = useSelector(dealerSelectors.getIsDueAtSigninAmountDeduct);
    const isEquityAllocationOrderEnabled = useSelector(featureToggleSelectors.isEquityAllocationOrderEnabled);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isPaymentGridEnabled = useSelector((state: StateTree) => offerReduxSelectors.getPaymentGridToggle(state, offerType));
    const isLocked = dealLockHooks.useIsDealLocked();
    const failed = useSelector(compositeSelectors.getLastFailureActionIfError(offerActionTypes.UPDATED_DOWN_PAYMENT_IMMEDIATE));

    const { value, handleChange, handleBlur, handleKeyDown } = usePaymentCall(
        offerReduxSelectors.getDownPayment,
        paymentInputActionCreators.updateDownPayment,
        { formatValueToString: formatNumberAndCents, autoSave: true }
    );

    // Cash down stays editable for a one-pay lease when Equity Allocation Order is enabled (DE752852).
    const disabled = (isOnePayLease && deductDueAtSigning && !isEquityAllocationOrderEnabled) || isLocked;
    const resolvedLabel = label ?? (isTargetCashAmountPaid ? CASH_AMOUNT_PAID : CASH_DOWN);

    return {
        label: resolvedLabel,
        value,
        disabled,
        readOnly: isPaymentGridEnabled,
        hasError: Boolean(failed),
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown
    };
};
